import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {Store} from '@ngrx/store';
import {AppState} from '@core/reducers';
import {ConsentActions, ConsentReducer} from '../store';
import {ActivatedRoute, Router} from '@angular/router';
import {UnitARequestModel} from '@core/modules/rest/consent/request/unit-a-request.model';
import {ConsentRequestModel} from '@core/modules/rest/consent/request/consent-request.model';
import {UnitStatusModel} from '../model/UnitStatus.model';
import {OutlineUnitBasicModel} from '@core/modules/rest/consent/response/outline-unit-basic.model';
import {UnitStatusEnum} from '@core/modules/rest/consent/enums/unit-status.enum';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {SkipDialogComponent} from '../dialogs/skip-dialog/skip-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {ConsentRestService} from '@core/modules/rest/consent/consent-rest.service';
import {LanguageStyleEnum} from '@core/enums/language-style.enum';
import {GetMoreUnitsConsentRequestModel} from '@core/modules/rest/consent/request/get-more-units-consent-request.model';
import {SubmitConsentRequestModel} from '@core/modules/rest/consent/request/submit-consent-request.model';
import {SubmitDialogComponent} from '../dialogs/submit-dialog/submit-dialog.component';
import {AbstractDialogModel} from '@shared/dialogs/model/abstract-dialog.model';
import {AuthRestService} from '@core/modules/rest/authentication/auth-rest.service';
import {MatDrawer} from '@angular/material/sidenav';
import {OutlineCommentsEnum} from '@core/enums/outline-comments.enum';
import {UnitPopupComponent} from '@shared/dialogs/unit-popup/unit-popup.component';
import {processHtml} from '../helpers/unit-parsing';
import {CommentBasicModel} from '@core/modules/rest/consent/response/comment-basic.model';
import {EditCommentRequestModel} from '@core/modules/rest/consent/request/edit-comment-request.model';
import {NotificationService} from '@shared/components/notification/service/notification.service';
import {PopupTypeEnum} from '@core/enums/popup-type.enum';
import {PresentationModeEnum} from '@core/enums/presentation-mode.enum';
import {FORM, PATIENT_CONSENT_OVERVIEW, QUESTIONNAIRE, SINGLE_COMMENT, WELCOME} from '@core/services/routes.service';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';
import {FinishReadingDialogEnum} from '@core/enums/finish-reading-dialog.enum';
import {WizardActions, WizardReducer} from '../../wizard/store';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TextSizeEnum} from '../enums/text-size.enum';
import {SECURED__WIZARD} from '@core/routes/app-routes';
import {getLanguageEnumByLanguageCode, LanguageEnum} from '@core/enums/language.enum';
import {LoggedUserDataModel} from '@core/modules/rest/user/response/logged-user-data.model';
import {CoreReducer} from '@core/store';
import {filter, take} from 'rxjs/operators';
import {UserSettingsModel} from '@core/modules/rest/user/response/user-settings.model';
import {LocationStrategy} from '@angular/common';
import {PersonalSettingsModel} from '@shared/model/personal-settings.model';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import * as fromCoreActions from '@core/store/core.actions';
import {OutlineChapterUnitBasicModel} from '@core/modules/rest/consent/response/outline-chapter-unit-basic.model';
import {populateFlatList} from '../helpers/helper-outline';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';

@UntilDestroy()
@Component({
    selector: 'app-display-consent-container',
    templateUrl: './display-consent-container.component.html',
    styleUrls: ['./display-consent-container.component.scss']
})
export class DisplayConsentContainerComponent implements OnInit, OnDestroy {
    treeData$: Observable<OutlineChapterUnitBasicModel[]> = this.store.select(ConsentReducer.consentSelector);
    currentUnit$: Observable<number> = this.store.select(ConsentReducer.currentUnitOrderIdSelector);
    comments$: Observable<CommentBasicModel[]> = this.store.select(ConsentReducer.commentSelector);
    loading$: Observable<boolean> = this.store.select(ConsentReducer.loadingSelector);
    loggedUserInfo$: Observable<LoggedUserDataModel> = this.store.select(CoreReducer.loggedUserDataSelector);
    wizardContent$: Observable<string> = this.store.select(WizardReducer.wizardContentSelector);

    currentUnitOrderId = 1;
    textSize = TextSizeEnum.SMALL;
    unitForHeader: OutlineUnitBasicModel | undefined;
    unitText: string | undefined;
    treeData: OutlineChapterUnitBasicModel[] = [];
    unitToDisplay: OutlineUnitBasicModel | undefined;
    unitPopUps: AbstractDialogModel[] | undefined;
    moreButtonDisabled = true;
    unitsFlat: OutlineUnitBasicModel[] = [];
    skipToUnits: OutlineUnitBasicModel[] = [];
    moreLoaded = false;
    initialUnitOrderIdList: number[] = [];
    moveToText: string | undefined;
    repeatButtonDisabled = true;
    pauseAudioSubject: Subject<void> = new Subject<void>();
    chapterUnits: UnitARequestModel[] = [];
    submittedConsentId?: string;
    alreadyLoadedUnits: OutlineUnitBasicModel[] = [];
    activeChapterUnit: OutlineChapterUnitBasicModel | undefined;
    showFinishBtn = false;
    openMenu: OutlineCommentsEnum | undefined;
    activeOutlineTab = 0;
    commentHereButtonDisabled = false;
    sideMenuOpen = false;
    backButtonText = '';
    previousUnitOrderId: number;
    skipBeginningUnitOderList: number[] = [];

    startTime: number = Date.now();
    totalTime = 0;

    audio = new Audio();
    ttsUrl: string | undefined;
    firstPlay = true;
    autoPlayVoice = false;
    ttsIconState = TtsControlEnum.playState;

    unitStandardStyle: OutlineUnitBasicModel | undefined;
    unitSimpleStyle: OutlineUnitBasicModel | undefined;
    unitEasyStyle: OutlineUnitBasicModel | undefined;
    selectedLanguageStyle: LanguageStyleEnum | undefined;

    isUserLeavingIndication = false;

    wizardContent: any;
    currentlyPlayingUnitOderId: number | undefined;
    language: LanguageEnum;

    loggedUserSettings: UserSettingsModel;
    personalSettings: PersonalSettingsModel;

    indicationTitle: string;
    informationUnitTitle: string;

    hideCloseIcon: boolean;

    constructor(
        private store: Store<AppState>,
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private translate: TranslateService,
        private consentService: ConsentRestService,
        private userService: UserRestService,
        private authService: AuthRestService,
        private notificationService: NotificationService,
        private ttsHelperService: TtsHelperService,
        private ttsService: TtsRestService,
        private location: LocationStrategy) {
        history.pushState(null, '', window.location.href);
        this.location.onPopState(() => {
            if (this.currentUnitOrderId && this.currentUnitOrderId > 1) {
                history.pushState(null, '', window.location.href);
                this.previousStep();
            }
        });
    }

    ngOnInit(): void {
        this.store.dispatch({type: '[Core] Consent page opened'});
        this.store.dispatch(fromCoreActions.GetLoggedUserData());
        this.audio.addEventListener('ended', this.setButtonTextOnEnd, false);
        this.backButtonText = this.translate.instant('GENERAL.BACK');
        this.language = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode')!);
        sessionStorage.getItem('audioMode') ? this.autoPlayVoice = true : this.autoPlayVoice = false;
        this.loggedUserInfo$.pipe(filter(value => !!value), take(1)).subscribe(loggedUserData => {
            if (loggedUserData) {
                this.loggedUserSettings = JSON.parse(loggedUserData.settings);
            }
            if (loggedUserData.personalSettings) {
                this.personalSettings = JSON.parse(loggedUserData.personalSettings);
                this.personalSettings = JSON.parse(loggedUserData.personalSettings);
                this.textSize = this.personalSettings.textSize;
            }
        });

        this.route.queryParamMap.subscribe(params => {
            this.submittedConsentId = params.get('consentId') as string;
            this.store.dispatch(WizardActions.GetWizardContent({consentId: this.submittedConsentId}));
            this.wizardContent$.pipe(untilDestroyed(this)).subscribe((wizardContent: any) => {
                if (wizardContent) {
                    this.wizardContent = wizardContent;
                    this.selectedLanguageStyle = this.wizardContent.languageStyle;

                    this.chapterUnits = [];
                    wizardContent.chapterUnits.forEach(x => {
                        const useCase = {
                            unitId: x.unitId,
                            unitLevel: x.unitLevel,
                            provisionDuration: wizardContent.provisionDuration
                        };
                        this.chapterUnits.push(useCase);
                    });
                    this.store.dispatch(ConsentActions.GetConsent({
                        consent: {
                            submittedConsentId: this.submittedConsentId,
                            chapterUnits: this.chapterUnits,
                            indicationId: this.wizardContent.indicationId,
                            language: this.wizardContent.language,
                            languageStyle: this.wizardContent.languageStyle
                        }
                    }));
                    this.currentUnit$.pipe(untilDestroyed(this)).subscribe(current => {
                        this.currentUnitOrderId = current;
                    });

                    this.treeData$.pipe(untilDestroyed(this)).subscribe((data) => {
                        if (data.length > 0 && this.treeData !== data) {
                            this.treeData = data;
                            this.populateFlatList();
                            this.setUnitToDisplayByOrderId(this.currentUnitOrderId);
                            if (this.unitToDisplay != null) {
                                this.setAlreadyLoadedUnits(this.unitToDisplay);
                            }
                            this.setSkipList();
                            this.setStartTime();
                        }
                    });

                    if (this.submittedConsentId) {
                        this.store.dispatch(ConsentActions.GetConsentComments({consentId: this.submittedConsentId}));
                    }
                }
            });
        });
    }

    private addUnitLanguageStyles(): void {
        switch (this.wizardContent.languageStyle) {
            case LanguageStyleEnum.SIMPLE:
                this.unitSimpleStyle = this.unitToDisplay;
                break;
            case LanguageStyleEnum.EASY:
                this.unitEasyStyle = this.unitToDisplay;
                break;
            default:
                this.unitStandardStyle = this.unitToDisplay;
        }
    }

    checkIfTTSShouldStartAutomatically(): void {
        const mode = this.personalSettings.presentationMode ? this.personalSettings.presentationMode : this.wizardContent.presentationMode;
        if (mode === PresentationModeEnum.VOICE || this.autoPlayVoice) {
            if (this.currentlyPlayingUnitOderId !== this.unitToDisplay.unitOrder) {
                this.convertTextToSpeech();
            }
        }
    }

    setSkipList(): void {
        this.skipToUnits = this.unitsFlat.filter(x => {
            if (this.unitToDisplay?.skipTo !== undefined) {
                return this.unitToDisplay.skipTo?.includes(x.unitOrder);
            }
            return false;
        });
    }

    setUnitToDisplayByOrderId(orderId: number): void {
        this.unitToDisplay = this.unitsFlat.find(x => x.unitOrder === orderId);
        this.setPreviousUnitData();
        this.unitPopUps = this.unitToDisplay?.popups;
        this.firstPlay = true;
        this.repeatButtonDisabled = true;
        const nextUnit = this.unitsFlat.find(x => x.unitOrder === this.unitToDisplay?.goAhead);
        this.addUnitLanguageStyles();
        if (this.unitToDisplay) {
            this.checkIfMoreButtonShouldBeEnabled();
            this.displayUnitToHeader(this.unitToDisplay);
            this.informationUnitTitle = this.unitToDisplay.informationUnitTitle;
            this.unitText = this.unitToDisplay.informationUnitInfo;
            const title = nextUnit?.informationUnitTitle.replace(/(<([^>]+)>)/gi, '');
            this.moveToText = this.translate.instant('CONSENT.MOVE_TO_BUTTON_DESC') + title;
            this.getCurrentUnit(this.unitToDisplay.unitOrder);
            this.showFinishBtn = this.unitToDisplay?.goAhead == null || this.unitToDisplay?.goAhead === 0;
            this.checkIfTTSShouldStartAutomatically();
        }
    }

    populateFlatList(): void {
        this.unitsFlat = populateFlatList(this.treeData);
        if (this.moreLoaded) {
            for (let i = 0; i < this.unitsFlat.length; i++) {
                if (this.unitsFlat[i].unitOrder === this.currentUnitOrderId) {
                    this.currentUnitOrderId = this.unitsFlat[i + 1].unitOrder;
                    break;
                }
            }
            this.moreLoaded = false;
        }
        if (this.initialUnitOrderIdList.length === 0) {
            this.unitsFlat.forEach(unit => {
                this.initialUnitOrderIdList.push(unit.unitOrder);
            });
        }
    }

    updateOutlineStatus(unitStatusModel: UnitStatusModel, viewDuration: number): void {
        this.store.dispatch(ConsentActions.MarkUnitStatus({
            unitOrderId: unitStatusModel.unitOrderId,
            status: unitStatusModel.unitStatus,
            viewDuration
        }));
    }

    onGoAhead(): void {
        this.stopAudio();
        this.backButtonText = this.unitToDisplay.informationUnitTitle;
        this.currentUnitOrderId = this.unitToDisplay ? this.unitToDisplay.goAhead : 1;
        this.updateUnitStatus();
        this.moreButtonDisabled = true;
        this.checkIfMoreButtonShouldBeEnabled();
        this.checkIfEndOfConsent();
        this.setStartTime();
        this.saveProgress();
    }

    updateUnitStatus(): void {
        const unitStatus: UnitStatusModel = {
            unitOrderId: this.unitToDisplay ? this.unitToDisplay.unitOrder : 1,
            unitStatus: UnitStatusEnum.UNDERSTOOD
        };
        this.updateOutlineStatus(unitStatus, this.calculateViewTime());
    }

    onCompleteIndication(): void {
        this.stopAudio();
        const unitToShowOnReturn = this.unitToDisplay ? this.unitToDisplay.goAhead : 1;
        this.currentUnitOrderId = unitToShowOnReturn !== null ? unitToShowOnReturn : this.currentUnitOrderId;
        const unitStatus: UnitStatusModel = {
            unitOrderId: this.unitToDisplay ? this.unitToDisplay.unitOrder : 1,
            unitStatus: UnitStatusEnum.UNDERSTOOD
        };
        this.updateOutlineStatus(unitStatus, this.calculateViewTime());
        this.saveProgress();
        this.navigateUserBasedOnSettings();
    }

    getRefForSubmitDialog(title: string, description: string): any {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.panelClass = 'submit-dialog-container';
        dialogConfig.data = {
            title,
            description,
            logout: true
        };
        return this.dialog.open(SubmitDialogComponent, dialogConfig);
    }

    onLeaveIndication(): void {
        this.stopAudio();
        const title = this.translate.instant('CONSENT.LEAVE_EDUCATION_TITLE');
        const description = this.translate.instant('CONSENT.LEAVE_EDUCATION_DESCRIPTION');
        const dialogRef = this.getRefForSubmitDialog(title, description);
        dialogRef.componentInstance.submitIndication.pipe(untilDestroyed(this)).subscribe((event: FinishReadingDialogEnum) => {
            this.isUserLeavingIndication = true;
            this.saveProgress();
            this.stopAudio();
            if (event === FinishReadingDialogEnum.OPEN_FORM) {
                this.router.navigate([FORM]);
            } else {
                this.authService.logout();
            }
        });
    }

    async onMore(): Promise<void> {
        if (this.unitToDisplay) {
            this.stopAudio();
            const nextUnitOrderId = this.unitToDisplay.goAhead;

            this.treeData$.pipe(untilDestroyed(this)).subscribe((data) => {
                this.activeChapterUnit = data.find(chapterUnit => chapterUnit.unitId === this.unitToDisplay?.chapterUnitId);
            });

            const consentRequestModel: ConsentRequestModel = {
                indicationId: this.unitToDisplay?.indicationId,
                language: this.wizardContent.language,
                languageStyle: this.wizardContent.languageStyle,
                chapterUnits: this.chapterUnits,
                submittedConsentId: this.submittedConsentId
            };

            const request: GetMoreUnitsConsentRequestModel = {
                unitOrderId: this.unitToDisplay.unitOrder,
                alreadyLoadedUnits: this.alreadyLoadedUnits,
                nextUnitOrderId,
                parentGoAhead: this.unitToDisplay.goAhead,
                parentSkip: this.unitToDisplay.skipTo,
                consent: consentRequestModel,
                activeChapterUnit: this.activeChapterUnit,
                viewDuration: this.calculateViewTime()
            };
            this.moreButtonDisabled = true;
            this.moreLoaded = true;
            this.loadMoreUnits(request);

            // TODO find a way to save progress AFTER store has been updated (load more units success)
            setTimeout(() => {
                this.saveProgress();
            }, 1000);

        }
    }

    loadMoreUnits(request: GetMoreUnitsConsentRequestModel): void {
        this.store.dispatch(ConsentActions.LoadMoreUnits({
            request
        }));
    }

    setAlreadyLoadedUnits(unit: OutlineUnitBasicModel): void {
        unit.children.forEach(item => {
            this.alreadyLoadedUnits = Object.assign([], this.alreadyLoadedUnits);
            this.alreadyLoadedUnits.push(item);
            if (unit.children.length > 0) {
                this.setAlreadyLoadedUnits(item);
            }
        });
    }

    checkIfMoreButtonShouldBeEnabled(): void {
        this.moreButtonDisabled = !(this.unitToDisplay.hasChildren && this.unitToDisplay.firstChildOrderId !== this.unitToDisplay.goAhead);
    }

    checkIfEndOfConsent(): void {
        if (this.unitToDisplay?.goAhead == null) {
            this.moveToText = '';
        }
    }

    onSkip(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.data = this.skipToUnits;
        dialogConfig.panelClass = 'skip-dialog-container';

        this.pause(false);
        const dialogRef = this.dialog.open(SkipDialogComponent, dialogConfig);
        dialogRef.afterClosed().pipe(untilDestroyed(this)).subscribe(result => {
            if (result !== undefined) {
                this.stopAudio();
                this.skipBeginningUnitOderList.push(this.unitToDisplay.unitOrder);
                this.currentUnitOrderId = result.event;
                this.markSkippedUnits(result.event);
                this.setUnitToDisplayIfNewLoadedUnitsAreSkipped(result.event);
                this.moreButtonDisabled = true;
                this.checkIfMoreButtonShouldBeEnabled();
                this.setStartTime();
                this.saveProgress();
            }
        });
    }

    setUnitToDisplayIfNewLoadedUnitsAreSkipped(unitOrderId: number): void {
        const firstToMark = this.unitToDisplay ? this.unitToDisplay.unitOrder : 0;
        const newlyLoadedUnitsSkipped = this.unitsFlat.filter(x => x.unitOrder < unitOrderId && x.unitOrder >= firstToMark).every(item => item.loadedWithMore === true);
        if (newlyLoadedUnitsSkipped) {
            this.setUnitToDisplayByOrderId(this.currentUnitOrderId);
        }
    }

    markSkippedUnits(targetUnit: number): void {
        const firstToMark = this.unitToDisplay ? this.unitToDisplay.unitOrder : 0;
        const unitsToMarkOrderIdList: number[] = [];
        this.unitsFlat.filter(x => x.unitOrder < targetUnit && x.unitOrder >= firstToMark).forEach(item => {
            unitsToMarkOrderIdList.push(item.unitOrder);
        });
        this.updateOutlineStatusForMultipleUnits(unitsToMarkOrderIdList, this.initialUnitOrderIdList, this.calculateViewTime());
    }

    onButtonClick(clickedBtn: string): void {
        switch (clickedBtn) {
            case 'more':
                this.onMore();
                break;
            case 'skip':
                this.onSkip();
                break;
            case 'next':
                this.onGoAhead();
                break;
            case 'complete':
                this.onCompleteIndication();
                break;
            default:
                break;
        }
        this.showFinishBtn = this.unitToDisplay?.goAhead == null || this.unitToDisplay?.goAhead === 0;
    }

    displayUnitToHeader(unit: OutlineUnitBasicModel): void {
        this.unitForHeader = unit;
    }

    getCurrentUnit(unitOrderId: number): void {
        this.currentUnitOrderId = unitOrderId;
    }

    convertTextToSpeech(): void {
        const removedHtmlTagsTitle = this.ttsHelperService.removeHTMLTags(this.unitToDisplay?.informationUnitTitle.replace(/(<([^>]+)>)/gi, ''));
        const removedHtmlTagsInfo = this.ttsHelperService.removeSSML(this.unitToDisplay?.informationUnitInfo);
        const finalText = removedHtmlTagsTitle.concat('. ').concat(removedHtmlTagsInfo);

        const pollyObject = {
            text: finalText,
            readingSpeed: this.ttsHelperService.getReadingSpeedByLanguageStyle(this.wizardContent.languageStyle),
            readingLanguage: this.wizardContent.language,
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonDisabled = false;
        this.ttsService.getTTS(pollyObject).pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.play();
                this.currentlyPlayingUnitOderId = this.unitToDisplay.unitOrder;
            }
        });
    }

    openPopUpDialog(props: AbstractDialogModel): void {
        this.pause(true);
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.panelClass = 'abstract-dialog-container';

        const images = props.id.split('_');
        const firstThatExist = images[0].toLowerCase();

        const popUpInfo = this.unitPopUps?.find(element => (element.id === firstThatExist && element.type === props.type));
        if (popUpInfo === undefined) {
            this.notificationService.info(this.translate.instant('NOTIFICATION.NO_POPUP_IMAGE'));
            return;
        }

        let cleanContent = popUpInfo.content!.replace(/\[(.*?)\]/gi, '');
        cleanContent = cleanContent.replace(/<b>&boxbox;<\/b>/g, '');
        cleanContent = cleanContent.replace(' ,', ',');

        const customBR = '[br]';
        cleanContent = cleanContent.replace(/<br>/g, customBR);
        const html: HTMLElement = processHtml(cleanContent);
        cleanContent = html.innerHTML.replace(/\[br\]/g, '<br>');

        dialogConfig.data = {
            id: props.id,
            indicationId: props.indicationId,
            type: popUpInfo.type,
            title: popUpInfo.type === PopupTypeEnum.TEXT ? props.title : popUpInfo.title,
            content: cleanContent,
            language: this.wizardContent.language,
            languageStyle: this.wizardContent.languageStyle,
            textSize: this.textSize
        };
        const dialogRef = this.dialog.open(UnitPopupComponent, dialogConfig);

        dialogRef.beforeClosed().pipe(untilDestroyed(this)).subscribe(() => {
            const audioWasPlayedInComponent = dialogRef.componentInstance.audioWasPlayedInComponent;
            if (audioWasPlayedInComponent) {
                this.ttsIconState = TtsControlEnum.playState;
            } else {
                this.resume(true);
            }
        });
    }

    submit(): void {
        this.saveProgress();
        this.stopAudio();
        this.authService.logout();
    }

    saveTextSize(): void {
        this.userService.updateTextSize(this.textSize);
    }

    saveProgress(): void {
        this.selectedLanguageStyle = this.wizardContent.languageStyle;
        let content;
        const contentWithUnitOrderId = {
            chapterUnits: this.treeData,
            currentUnitOrderId: this.currentUnitOrderId,
            textSize: this.textSize
        };
        content = JSON.stringify(contentWithUnitOrderId);

        const request: SubmitConsentRequestModel = {
            id: this.submittedConsentId,
            indicationId: this.wizardContent.indicationId,
            content,
            readingTime: this.totalTime,
            consentStarted: false,
            consentUpdated: true,
            languageEnum: this.language
        };

        this.store.dispatch(ConsentActions.SubmitConsent({
            request
        }));
    }

    toggleMenu(drawer: MatDrawer, menu: OutlineCommentsEnum): void {
        this.setActiveTab(menu);
        if (this.openMenu === undefined || this.openMenu === menu) {
            drawer.toggle();
        } else {
            if (!drawer.opened) {
                drawer.open();
            }
        }
        this.openMenu = menu;
    }

    openCommentMenu(drawer: MatDrawer): void {
        drawer.open();
        this.setActiveTab(OutlineCommentsEnum.COMMENTS);
        this.openMenu = OutlineCommentsEnum.COMMENTS;
    }

    private setActiveTab(menu: OutlineCommentsEnum): void {
        if (menu === OutlineCommentsEnum.OUTLINE) {
            this.commentHereButtonDisabled = false;
            if (this.activeOutlineTab === 1) {
                this.activeOutlineTab = 0;
            } else {
                this.activeOutlineTab = 1;
            }
        } else if (menu === OutlineCommentsEnum.COMMENTS) {
            if (this.activeOutlineTab === 2) {
                this.activeOutlineTab = 0;
                this.commentHereButtonDisabled = false;
                this.sideMenuOpen = false;
            } else {
                this.activeOutlineTab = 2;
                this.commentHereButtonDisabled = true;
                this.sideMenuOpen = true;
            }
        } else {
            this.commentHereButtonDisabled = false;
            if (this.activeOutlineTab === 3) {
                this.activeOutlineTab = 0;
            } else {
                this.activeOutlineTab = 3;
            }
        }
    }

    submitComment(comment: string): void {
        if (this.unitToDisplay) {
            this.store.dispatch(ConsentActions.SubmitComment({
                request: {
                    submittedConsentId: this.submittedConsentId,
                    informationUnitTitle: this.unitToDisplay.informationUnitTitle,
                    content: comment
                }
            }));
        }
    }

    deleteComment(commentId: string): void {
        this.store.dispatch(ConsentActions.DeleteComment({
            consentId: this.submittedConsentId,
            commentId
        }));
        this.notificationService.success(this.translate.instant('CONSENT.COMMENTS.DELETED'));
    }

    editComment(editComObj: EditCommentRequestModel): void {
        const finalEditObj = {
            ...editComObj,
            submittedConsentId: this.submittedConsentId
        };
        this.store.dispatch(ConsentActions.EditComment({request: finalEditObj}));
        this.notificationService.success(this.translate.instant('CONSENT.COMMENTS.EDITED'));
    }

    closeMenu(drawer: MatDrawer): void {
        drawer.toggle();
        this.activeOutlineTab = 0;
        this.sideMenuOpen = false;
        this.commentHereButtonDisabled = false;
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.play();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resume(false);
                } else {
                    this.pause(false);
                }
            }
        }
    }

    play(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    stopAudio(): void {
        this.pause(false);
    }

    pause(automatic: boolean): void {
        if (!automatic) {
            this.ttsIconState = TtsControlEnum.playState;
        }
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    resume(automatic: boolean): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState && automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
            if (this.ttsIconState === TtsControlEnum.playState && !automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    setButtonTextOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

    @HostListener('window:popstate', ['$event'])
    onPopState(): void {
        this.pause(false);
    }

    private setStartTime(): void {
        this.startTime = Date.now();
    }

    private calculateViewTime(): number {
        const endTime = Date.now();
        const timeElapsed = endTime - this.startTime;
        const prevTime = this.unitToDisplay.viewDurationInMilliseconds;

        if (prevTime > 0 && prevTime < timeElapsed) {
            this.totalTime -= prevTime;
            this.totalTime += timeElapsed;
            return timeElapsed;
        } else if (prevTime > 0 && prevTime > timeElapsed) {
            return prevTime;
        }
        this.totalTime += timeElapsed;
        return timeElapsed;
    }

    updateOutlineStatusForMultipleUnits(unitsToMark: number[], initialUnitOrderIdList: number[], viewDuration: number): void {
        this.store.dispatch(ConsentActions.MarkSkippedUnitsStatuses({
            unitsToMark,
            initialUnitOrderIdList,
            viewDuration
        }));
    }

    previewChosenUnit(unitOrder: number): void {
        this.stopAudio();
        this.updateUnitStatus();
        this.saveProgress();
        this.setUnitToDisplayByOrderId(unitOrder);
        this.setSkipList();
        this.setStartTime();
    }

    temporaryLanguageStyleChange(lang: LanguageStyleEnum): void {
        this.selectedLanguageStyle = lang;
        this.consentService.getUnitInLanguageStyle(this.unitToDisplay!.informationUnitId, this.unitToDisplay!.indicationId, this.wizardContent.language, lang)
            .pipe(untilDestroyed(this))
            .subscribe(unit => {
                    this.unitText = unit.informationUnitInfo;
                    this.unitForHeader = unit;
                    this.unitPopUps = unit.popups;
                }
            );
    }

    getBackToWizard(): void {
        this.router.navigate([SECURED__WIZARD], {
            queryParams: {
                consentId: this.submittedConsentId
            }
        });
    }

    markQuestionnaireAsStarted(): void {
        this.consentService.markQuestionnaireAsStarted(this.submittedConsentId).pipe(untilDestroyed(this)).subscribe();
    }

    changeTextSize(event: TextSizeEnum): void {
        this.textSize = event;
        this.saveTextSize();
    }

    navigateUserBasedOnSettings(): void {
        if (!this.loggedUserSettings.commentingSingleTypeOff) {
            this.router.navigate([SINGLE_COMMENT], {queryParams: {consentId: this.submittedConsentId}});
        } else if (this.loggedUserSettings.anamnesisQuestionnaireOff) {
            this.consentService.markOverviewAsStarted(this.submittedConsentId).pipe(untilDestroyed(this)).subscribe();
            this.router.navigate([PATIENT_CONSENT_OVERVIEW(this.submittedConsentId)]);
        } else {
            this.markQuestionnaireAsStarted();
            this.router.navigate([QUESTIONNAIRE(this.submittedConsentId)]);
        }
    }

    previousStep(): void {
        if (this.currentUnitOrderId === 1) {
            if (this.wizardContent.missingData !== undefined && this.wizardContent.missingData.length > 0) {
                this.stopAudio();
                this.getBackToWizard();
            } else {
                this.router.navigate([WELCOME]);
            }
        } else {
            this.previewChosenUnit(this.previousUnitOrderId);
        }
    }

    setPreviousUnitData(): void {
        const previousUnit = this.findPreviousUnit();
        if (this.unitToDisplay && this.unitToDisplay.unitOrder > 1) {
            this.backButtonText = previousUnit.informationUnitTitle;
            this.previousUnitOrderId = previousUnit.unitOrder;
        } else {
            this.backButtonText = this.translate.instant('GENERAL.BACK');
        }
    }

    findPreviousUnit(): OutlineUnitBasicModel {
        const currentUnitIndex = this.unitsFlat.indexOf(this.unitToDisplay);
        let previousUnit;

        this.discardOldSkippedUnits();

        if (this.skipBeginningUnitOderList.length > 0) {
            previousUnit = this.unitsFlat.find(unit => unit.unitOrder === this.skipBeginningUnitOderList[this.skipBeginningUnitOderList.length - 1]);
        } else {
            previousUnit = this.unitsFlat[0];
        }

        for (let i = 1; i <= currentUnitIndex; i++) {
            if (this.unitsFlat[currentUnitIndex - i].status === UnitStatusEnum.UNDERSTOOD) {
                previousUnit = this.unitsFlat[currentUnitIndex - i];
                break;
            }
        }

        return previousUnit;
    }

    discardOldSkippedUnits(): void {
        for (let i = this.skipBeginningUnitOderList.length - 1; i >= 0; i--) {
            if (this.skipBeginningUnitOderList[i] >= this.unitToDisplay.unitOrder) {
                this.skipBeginningUnitOderList.splice(i, 1);
            } else {
                break;
            }
        }
    }

    ngOnDestroy(): void {
        this.pause(false);
    }
}
